/**
 * API Types - Centralni tipovi za sve API operacije
 */

// ============================================
// Generic API Response Types
// ============================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface ApiListResponse<T> extends ApiResponse<T[]> {
  total?: number;
}

// ============================================
// CSV File Types
// ============================================

export interface FileData {
  fileName?: string;
  bezeichnung?: string;
  utcMin?: string;
  utcMax?: string;
  zeitschrittweite?: number | string;
  min?: number | string;
  max?: number | string;
  offset?: number | string;
  datenpunkte?: number | string;
  numerischeDatenpunkte?: number | string;
  numerischerAnteil?: number | string;
  datenform?: string;
  zeithorizontStart?: string;
  zeithorizontEnd?: string;
  zeitschrittweiteTransferiertenDaten?: number | string;
  offsetTransferiertenDaten?: number | string;
  mittelwertbildungÜberDenZeithorizont?: boolean;
  datenanpassung?: string;
  zeitschrittweiteMinValue?: number | string;
  zeitschrittweiteAvgValue?: number | string;
  skalierung?: string;
  skalierungMax?: number | string;
  skalierungMin?: number | string;
  storagePath?: string;
  type?: 'input' | 'output';
  fileSize?: number;
}

export interface CreateFileData extends FileData {
  fileName: string;
}

export type UpdateFileData = Partial<FileData>;

export interface CSVFileResponse {
  id: string;
  session_id: string;
  file_name: string;
  bezeichnung?: string;
  storage_path?: string;
  type?: 'input' | 'output';
  created_at?: string;
  updated_at?: string;
  // Allow additional properties for backward compatibility
  [key: string]: unknown;
}

// ============================================
// Time Info Types
// ============================================

export interface TimeInfoData {
  jahr?: boolean;
  monat?: boolean;
  woche?: boolean;
  tag?: boolean;
  feiertag?: boolean;
  zeitzone?: string;
  category_data?: Record<string, unknown>;
}

export interface TimeInfoResponse extends TimeInfoData {
  id: string;
  session_id: string;
  created_at?: string;
  updated_at?: string;
}

// ============================================
// Zeitschritte Types
// ============================================

export interface ZeitschritteData {
  eingabe: string;
  ausgabe: string;
  zeitschrittweite: string;
  offset: string;
}

export interface ZeitschritteResponse extends ZeitschritteData {
  id: string;
  session_id: string;
  created_at?: string;
  updated_at?: string;
}

// ============================================
// Session Types
// ============================================

export interface SessionData {
  n_dat?: string;
  finalized?: boolean;
  file_count?: number;
}

export interface SessionResponse extends SessionData {
  id: string;
  sessionId?: string;
  created_at?: string;
  updated_at?: string;
  success?: boolean;
  error?: string;
  // Allow additional properties
  [key: string]: unknown;
}

export interface SessionWithDetailsResponse {
  id: string;
  n_dat?: string;
  finalized?: boolean;
  file_count?: number;
  created_at?: string;
  updated_at?: string;
  session?: {
    n_dat?: string;
    finalized?: boolean;
    file_count?: number;
  };
  timeInfo?: TimeInfoResponse | null;
  zeitschritte?: ZeitschritteResponse | null;
  files?: CSVFileResponse[];
}

export interface SessionListItem {
  sessionId: string;
  sessionName?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  finalized?: boolean;
  fileCount?: number;
  lastUpdated?: string;
  // Allow additional properties
  [key: string]: unknown;
}

export interface SessionListResponse {
  success: boolean;
  sessions: SessionListItem[];
  error?: string;
}

// ============================================
// Training Types
// ============================================

export interface TrainingStatusResponse {
  success: boolean;
  status?: string;
  progress?: number;
  current_step?: string;
  completed_at?: string | null;
  message?: string;
  error?: string;
}

export interface ScalersResponse {
  success: boolean;
  scalers?: Record<string, unknown>;
  session_id?: string;
  downloaded_at?: string;
  format?: string;
  message?: string;
  error?: string;
  // Allow additional properties for backward compatibility
  [key: string]: unknown;
}

export interface EvaluationTablesResponse {
  success: boolean;
  tables?: Record<string, unknown>;
  error?: string;
}

// ============================================
// Plot Types
// ============================================

export interface PlotData {
  session_id: string;
  df_plot_in: Record<string, unknown>;
  df_plot_out: Record<string, unknown>;
  df_plot_fcst: Record<string, unknown>;
}

export interface PlotResponse {
  success: boolean;
  plot?: string;
  plot_data?: {
    df_plot_in?: Record<string, unknown>;
    df_plot_out?: Record<string, unknown>;
    df_plot_fcst?: Record<string, unknown>;
  };
  error?: string;
}

export interface PlotVariablesResponse {
  success: boolean;
  variables?: string[];
  input_variables?: string[];
  output_variables?: string[];
  time_components?: string[];
  error?: string;
}

// ============================================
// Field Mapping Types (for snake_case <-> camelCase)
// ============================================

export type FieldMappingKey = keyof FileData;

export const FILE_FIELD_MAPPING: Record<string, string> = {
  fileName: 'file_name',
  bezeichnung: 'bezeichnung',
  utcMin: 'utc_min',
  utcMax: 'utc_max',
  zeitschrittweite: 'zeitschrittweite',
  min: 'min',
  max: 'max',
  offset: 'offset',
  datenpunkte: 'datenpunkte',
  numerischeDatenpunkte: 'numerische_datenpunkte',
  numerischerAnteil: 'numerischer_anteil',
  datenform: 'datenform',
  zeithorizontStart: 'zeithorizont_start',
  zeithorizontEnd: 'zeithorizont_end',
  zeitschrittweiteTransferiertenDaten: 'zeitschrittweite_transferierten_daten',
  offsetTransferiertenDaten: 'offset_transferierten_daten',
  'mittelwertbildungÜberDenZeithorizont': 'mittelwertbildung_uber_den_zeithorizont',
  datenanpassung: 'datenanpassung',
  zeitschrittweiteMinValue: 'zeitschrittweite_min',
  zeitschrittweiteAvgValue: 'zeitschrittweite_mittelwert',
  skalierung: 'skalierung',
  skalierungMax: 'skalierung_max',
  skalierungMin: 'skalierung_min',
  storagePath: 'storage_path',
  type: 'type',
  fileSize: 'file_size',
};

export const NULLABLE_FIELDS = [
  'zeithorizont_start',
  'zeithorizont_end',
  'zeitschrittweite_transferierten_daten',
  'offset_transferierten_daten',
  'min',
  'max',
  'offset',
  'datenpunkte',
  'numerische_datenpunkte',
  'numerischer_anteil',
  'zeitschrittweite',
  'zeitschrittweite_min',
  'zeitschrittweite_mittelwert',
  'skalierung_max',
  'skalierung_min',
  'utc_min',
  'utc_max',
];
